<template>
  <div class="conv-header">
    <div class="conversation-header--details">
      <div class="user">
        <back-button-chat-list v-show="showBackButton" />
        <Thumbnail
          :src="currentContact.thumbnail"
          :badge="inboxBadge"
          :username="currentContact.name"
          :status="currentContact.availability_status"
        />
        <div class="user--profile__meta">
          <h3 class="user--name text-truncate">
            <template v-if="isPhoneNumber">
              <a :href="'tel:' + currentContact.name" class="user--name-link">
                &ZeroWidthSpace;{{ currentContact.name }}
              </a>
            </template>
            <template v-else>
              &ZeroWidthSpace;{{ currentContact.name }}
              <fluent-icon
                v-if="!isHMACVerified"
                v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
                size="14"
                class="hmac-warning__icon"
                icon="warning"
              />
            </template>
          </h3>
          <div class="conversation--header--actions text-truncate">
            <inbox-name
              v-if="hasMultipleInboxes"
              :inbox="inbox"
              class="margin-right-small header-inbox-name"
            />
            <span
              v-if="isSnoozed"
              class="snoozed--display-text margin-right-small"
            >
              {{ snoozedDisplayText }}
            </span>
            <woot-button
              v-if="activeIndex === 0"
              class="user--profile__button margin-right-small"
              size="small"
              variant="link"
              @click="$emit('contact-panel-toggle')"
            >
              {{ contactPanelToggleText }}
            </woot-button>
          </div>
        </div>
      </div>
      <div
        class="header-actions-wrap"
        :class="{ 'has-open-sidebar': isContactPanelOpen }"
      >
        <more-actions
          :is-intertel-u-c-inbox="isIntertelUCInbox"
          :pbx="inbox.pbx"
        />
      </div>
    </div>
  </div>
</template>
<script>
import { isPhoneE164OrEmpty } from 'shared/helpers/Validators';
import { hasPressedAltAndOKey } from 'shared/helpers/KeyboardHelpers';
import { mapGetters } from 'vuex';
import agentMixin from '../../../mixins/agentMixin.js';
import BackButtonChatList from '../BackButtonChatList';
import differenceInHours from 'date-fns/differenceInHours';
import eventListenerMixins from 'shared/mixins/eventListenerMixins';
import inboxMixin from 'shared/mixins/inboxMixin';
import InboxName from '../InboxName';
import MoreActions from './MoreActions';
import Thumbnail from '../Thumbnail';
import wootConstants from '../../../constants';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
export default {
  components: {
    InboxName,
    MoreActions,
    Thumbnail,
    BackButtonChatList,
  },
  mixins: [inboxMixin, agentMixin, eventListenerMixins],
  props: {
    chat: {
      type: Object,
      default: () => {},
    },
    isContactPanelOpen: {
      type: Boolean,
      default: false,
    },
    showBackButton: {
      type: Boolean,
      default: false,
    },
    activeIndex: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxAssignableAgents/getUIFlags',
      currentChat: 'getSelectedChat',
    }),
    chatMetadata() {
      return this.chat.meta;
    },
    backButtonUrl() {
      const {
        params: { accountId, inbox_id: inboxId, label, teamId },
        name,
      } = this.$route;
      return conversationListPageURL({
        accountId,
        inboxId,
        label,
        teamId,
        conversationType: name === 'conversation_mentions' ? 'mention' : '',
      });
    },
    isHMACVerified() {
      if (!this.isAWebWidgetInbox) {
        return true;
      }
      return this.chatMetadata.hmac_verified;
    },
    currentContact() {
      return this.$store.getters['contacts/getContact'](
        this.chat.meta.sender.id
      );
    },
    isSnoozed() {
      return this.currentChat.status === wootConstants.STATUS_TYPE.SNOOZED;
    },
    snoozedDisplayText() {
      const { snoozed_until: snoozedUntil } = this.currentChat;
      if (snoozedUntil) {
        // When the snooze is applied, it schedules the unsnooze event to next day/week 9AM.
        // By that logic if the time difference is less than or equal to 24 + 9 hours we can consider it tomorrow.
        const MAX_TIME_DIFFERENCE = 33;
        const isSnoozedUntilTomorrow =
          differenceInHours(new Date(snoozedUntil), new Date()) <=
          MAX_TIME_DIFFERENCE;
        return this.$t(
          isSnoozedUntilTomorrow
            ? 'CONVERSATION.HEADER.SNOOZED_UNTIL_TOMORROW'
            : 'CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_WEEK'
        );
      }
      return this.$t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
    },
    /**
     * Retorna el texto del botón para abrir o cerrar el panel de contacto.
     * El texto varía según si el panel está abierto o cerrado, usando traducciones.
     */
    contactPanelToggleText() {
      return `${
        this.isContactPanelOpen
          ? this.$t('CONVERSATION.HEADER.CLOSE')
          : this.$t('CONVERSATION.HEADER.OPEN')
      } ${this.$t('CONVERSATION.HEADER.DETAILS')}`;
    },
    inbox() {
      const { inbox_id: inboxId } = this.chat;
      return this.$store.getters['inboxes/getInbox'](inboxId);
    },
    hasMultipleInboxes() {
      return this.$store.getters['inboxes/getInboxes'].length > 1;
    },

    // Computed property para la validación de número de teléfono
    isPhoneNumber() {
      return isPhoneE164OrEmpty(this.currentContact.name);
    },
  },

  methods: {
    handleKeyEvents(e) {
      if (hasPressedAltAndOKey(e)) {
        this.$emit('contact-panel-toggle');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~dashboard/assets/scss/woot';

.conv-header {
  flex: 0 0 var(--space-jumbo);
  flex-direction: row;

  @include breakpoint(medium up) {
    flex-direction: column;
  }
}

.conversation-header--details {
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  width: 100%;

  @include breakpoint(medium up) {
    flex-direction: row;
  }
}

.option__desc {
  display: flex;
  align-items: center;
}

.option__desc {
  &::v-deep .status-badge {
    margin-right: var(--space-small);
    min-width: 0;
    flex-shrink: 0;
  }
}

.user--name {
  display: inline-block;
  font-size: var(--font-size-medium);
  line-height: 1.3;
  margin: 0;
  text-transform: capitalize;
  width: 100%;
}

.user--name-link {
  font-size: var(--font-size-medium);
  line-height: 1.3;
  margin: 0;
  width: 100%;
  font-weight: var(--font-weight-normal);
  color: var(--color-heading);
}

.conversation--header--actions {
  align-items: center;
  display: flex;
  font-size: var(--font-size-mini);

  .user--profile__button {
    padding: 0;
  }

  .snoozed--display-text {
    font-weight: var(--font-weight-medium);
    color: var(--y-600);
  }
}

.hmac-warning__icon {
  color: var(--y-600);
}

.header-inbox-name {
  max-width: unset;
}
</style>
